import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { History, Search, RefreshCw, Plus, Edit3, Trash2, Filter, User, ShieldCheck, Crown, UserCog, X, Clock, Hash, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

const ACTION_CFG = {
  create: { Icon: Plus, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  update: { Icon: Edit3, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  delete: { Icon: Trash2, cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const ROLE_ICON = {
  super_admin: Crown,
  area_manager: UserCog,
  supervisor: ShieldCheck,
};

function relativeTime(iso, lang) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return lang === "ar" ? "الآن" : "just now";
  if (min < 60) return lang === "ar" ? `قبل ${min} د` : `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return lang === "ar" ? `قبل ${hr} س` : `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return lang === "ar" ? `قبل ${day} يوم` : `${day}d ago`;
  return d.toLocaleDateString(lang === "ar" ? "ar-SA-u-ca-gregory" : "en-US");
}

function Detail({ label, value, mono, icon: Icon }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0">
      {Icon && <Icon size={13} className="text-teal-600 mt-0.5 shrink-0" />}
      <div className="text-[11px] text-gray-500 w-24 shrink-0">{label}</div>
      <div className={`flex-1 text-sm text-gray-900 font-medium break-words ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}

export default function AuditLog() {
  const { t, lang } = useT();
  const [data, setData] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entity: "", action: "", days: 30 });
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity) params.set("entity", filters.entity);
      if (filters.action) params.set("action", filters.action);
      if (filters.days) params.set("days", String(filters.days));
      params.set("limit", "500");
      const res = await api.get(`/audit-log?${params.toString()}`);
      setData(res.data || { items: [], count: 0 });
    } catch (e) {
      setData({ items: [], count: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.entity, filters.action, filters.days]);

  const filtered = useMemo(() => {
    if (!q) return data.items;
    const s = q.toLowerCase();
    return data.items.filter((it) =>
      (it.entity_name || "").toLowerCase().includes(s) ||
      (it.actor_username || "").toLowerCase().includes(s) ||
      (it.actor_name || "").toLowerCase().includes(s)
    );
  }, [q, data.items]);

  return (
    <div className="space-y-5" data-testid="audit-log-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-teal-900 flex items-center gap-2">
            <History size={20} /> {t("nav_audit_log")}
          </h2>
          <p className="text-sm text-gray-500">{t("audit_log_subtitle")}</p>
        </div>
        <button onClick={load} className="btn btn-secondary" data-testid="audit-refresh"><RefreshCw size={14} /> {t("refresh")}</button>
      </div>

      <div className="bg-white rounded-2xl card-shadow p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="inp-label flex items-center gap-1"><Search size={12} /> {t("search")}</label>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("audit_search_placeholder")}
              className="inp"
              data-testid="audit-search-input"
            />
          </div>
          <div>
            <label className="inp-label flex items-center gap-1"><Filter size={12} /> {t("audit_filter_entity")}</label>
            <select
              value={filters.entity}
              onChange={(e) => setFilters({ ...filters, entity: e.target.value })}
              className="inp"
              data-testid="audit-filter-entity"
            >
              <option value="">{t("all")}</option>
              <option value="employee">{t("entity_employee")}</option>
              <option value="branch">{t("entity_branch")}</option>
              <option value="admin">{t("entity_admin")}</option>
            </select>
          </div>
          <div>
            <label className="inp-label flex items-center gap-1">{t("audit_filter_action")}</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="inp"
              data-testid="audit-filter-action"
            >
              <option value="">{t("all")}</option>
              <option value="create">{t("action_create")}</option>
              <option value="update">{t("action_update")}</option>
              <option value="delete">{t("action_delete")}</option>
            </select>
          </div>
          <div>
            <label className="inp-label">{t("audit_filter_days")}</label>
            <select
              value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value, 10) })}
              className="inp"
              data-testid="audit-filter-days"
            >
              <option value={7}>{t("days_7")}</option>
              <option value={30}>{t("days_30")}</option>
              <option value={90}>{t("days_90")}</option>
              <option value={180}>{t("days_180")}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl card-shadow overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm" data-testid="audit-loading">{t("loading")}…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm" data-testid="audit-empty">{t("audit_empty")}</div>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="audit-list">
            {filtered.map((it) => {
              const cfg = ACTION_CFG[it.action] || ACTION_CFG.update;
              const RoleIcon = ROLE_ICON[it.actor_role] || User;
              return (
                <li key={it.id} className="px-4 py-3 hover:bg-gray-50 flex items-start gap-3 cursor-pointer" onClick={() => setDetail(it)} data-testid={`audit-row-${it.id}`}>
                  <div className={`rounded-full p-2 border ${cfg.cls} shrink-0`}>
                    <cfg.Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`pill ${cfg.cls} font-semibold`}>
                        {t(`action_${it.action}`)}
                      </span>
                      <span className="text-sm text-gray-700">{t(`entity_${it.entity}`)}</span>
                      {it.entity_name && (
                        <span className="text-sm font-semibold text-teal-900 truncate">«{it.entity_name}»</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 flex-wrap">
                      <RoleIcon size={11} className="text-gray-400" />
                      <span>{it.actor_name || it.actor_username}</span>
                      <span className="text-gray-300">•</span>
                      <span title={new Date(it.ts).toLocaleString()}>{relativeTime(it.ts, lang)}</span>
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-gray-300 self-center shrink-0" />
                </li>
              );
            })}
          </ul>
        )}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">
            {t("showing_records").replace("{count}", filtered.length)}
          </div>
        )}
      </div>

      {detail && createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 modal-backdrop animate-fade-in" onClick={() => setDetail(null)} data-testid="audit-detail-modal">
          <div className="bg-white w-full sm:max-w-md max-h-[90vh] rounded-t-2xl sm:rounded-2xl card-shadow overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2 text-teal-900">
                <History size={18} />
                <h3 className="font-bold">{t("audit_detail_title")}</h3>
              </div>
              <button onClick={() => setDetail(null)} className="bg-gray-100 hover:bg-gray-200 rounded-full p-2" data-testid="audit-detail-close">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className={`pill ${(ACTION_CFG[detail.action] || ACTION_CFG.update).cls} text-sm font-semibold w-fit`}>
                {React.createElement((ACTION_CFG[detail.action] || ACTION_CFG.update).Icon, { size: 12 })}
                {t(`action_${detail.action}`)} — {t(`entity_${detail.entity}`)}
              </div>
              <Detail label={t("entity_name")} value={detail.entity_name || "—"} />
              <Detail label={t("performed_by")} value={`${detail.actor_name || detail.actor_username} (@${detail.actor_username})`} icon={ROLE_ICON[detail.actor_role] || User} />
              <Detail label={t("role")} value={detail.actor_role === "super_admin" ? t("role_super_admin") : detail.actor_role === "area_manager" ? t("role_area_manager") : t("role_supervisor")} />
              <Detail label={t("when")} value={new Date(detail.ts).toLocaleString(lang === "ar" ? "ar-SA-u-ca-gregory" : "en-US")} icon={Clock} />
              <Detail label="ID" value={detail.entity_id} mono icon={Hash} />
              {detail.area_manager_id && (
                <Detail label={t("area_manager")} value={detail.area_manager_id} mono />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
